import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';

export const gamePackageAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'GAME_CATALOG_API_BASE_URL',
});
attachAuthInterceptors(gamePackageAPI);

export interface GetGamePackagesRequest {
  page?: number;
  limit?: number;
  search?: string;
  game_id?: string;
  game_name?: string;
  supplier_ids?: string[];
  game_topup_type_ids?: string[];
  is_active?: boolean;
  is_suspended?: boolean;
  include_deleted?: boolean;
  all?: boolean;
}

export interface GetGamePackageOptionsRequest {
  game_id?: string | string[];
  supplier_ids?: string[];
  game_topup_type_ids?: string[];
  include_deleted?: boolean;
  include_inactive?: boolean;
  for_topup?: boolean;
}

export interface GamePackageOption {
  id: string;
  name: string;
}

export interface GetGamePackageOptionsResponse {
  success: boolean;
  data?: GamePackageOption[];
  message?: string;
}

export interface SupplierPriceItem {
  supplier_id: string;
  supplier_key: string;
  supplier_name: string;
  game_topup_type_id: string;
  topup_type_name: string;
  original_price: number;
  price: number;
  discount_info: string | null;
}

export interface GamePackageData {
  id: string;
  game_id: string;
  game_name: string;
  name: string;
  element_name: string;
  shell_cost?: number | null;
  description: string;
  price: number;
  image_url: string;
  operation_type: string;
  source: string;
  is_active: boolean;
  is_deleted: boolean;
  is_suspended: boolean;
  suspended_reason?: string;
  suspended_max_loss?: number;
  suspended_supplier_name?: string;
  suspended_game_topup_type_name?: string;
  suspended_supplier_id?: string;
  suspended_game_topup_type_id?: string;
  created_at: string;
  updated_at: string;
  created_by_id: string;
  created_by_username: string;
  updated_by_id: string;
  updated_by_username: string;
  supplier_id?: string;
  supplier_name?: string;
  game_topup_type_id?: string;
  game_topup_type_name?: string;
  // Markup fields (available when group_by_supplier=true)
  has_markup?: boolean;
  markup_is_active?: boolean;
  markup_value?: number;
  markup_amount?: number;
  selling_price?: number;
  suppliers?: SupplierPriceItem[];
}

export interface GetGamePackagesResponse {
  success: boolean;
  data?: {
    data: GamePackageData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export const getGamePackages = async (
  params: GetGamePackagesRequest
): Promise<GetGamePackagesResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      game_id,
      game_name,
      is_active,
      is_suspended,
      include_deleted,
      all,
      supplier_ids,
      game_topup_type_ids,
    } = params;

    const body: Record<string, any> = {
      group_by_supplier: true,
    };

    if (!all) {
      body.page = page;
      body.limit = limit;
    } else {
      body.all = true;
    }

    if (search) body.search = search;
    if (game_id) body.game_id = game_id;
    if (game_name) body.game_name = game_name;
    if (is_active !== undefined) body.is_active = is_active;
    if (is_suspended !== undefined) body.is_suspended = is_suspended;
    if (include_deleted !== undefined) body.include_deleted = include_deleted;
    if (supplier_ids && supplier_ids.length > 0)
      body.supplier_ids = supplier_ids;
    if (game_topup_type_ids && game_topup_type_ids.length > 0)
      body.game_topup_type_ids = game_topup_type_ids;

    const response = await gamePackageAPI.post('/game-packages/search', body);

    return {
      success: true,
      data: {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: response.data.message || 'Game packages fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game packages',
    };
  }
};

export interface GamePackageSummaryTopupOption {
  supplier_name: string;
  topup_type_name: string;
}

export interface GamePackageSummaryItem {
  id: string;
  package_name: string;
  suppliers: string[];
  is_active: boolean;
  topup_options: GamePackageSummaryTopupOption[];
}

export interface GetGamePackagesSummaryResponse {
  success: boolean;
  data?: GamePackageSummaryItem[];
  message?: string;
}

export const getGamePackagesSummary = async ({
  game_id,
}: {
  game_id: string;
}): Promise<GetGamePackagesSummaryResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('game_id', game_id);

    const response = await gamePackageAPI.get(
      `/game-packages/summary?${queryParams.toString()}`
    );

    const list = response.data?.data ?? response.data;
    return {
      success: true,
      data: Array.isArray(list) ? list : [],
      message:
        response.data?.message || 'Game packages summary fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game packages summary',
    };
  }
};

export interface SupplierPriceItem {
  supplier_id: string;
  game_topup_type_id: string;
  price: number;
}

export interface CreateGamePackageRequest {
  game_id: string;
  name: string;
  element_name?: string;
  description: string;
  is_active: boolean;
  shell_cost?: number | null;
  image_file?: File;
  supplier_prices?: SupplierPriceItem[];
}

export interface CreateGamePackageResponse {
  success: boolean;
  data?: GamePackageData;
  message?: string;
  /** API error code when `success` is false (e.g. duplicate name). */
  code?: string;
  /** HTTP status when `success` is false. */
  status?: number;
}

/** Package name already exists for the selected game (409 / ERR_GAME_022). */
export const ERR_GAME_PACKAGE_DUPLICATE_NAME = 'ERR_GAME_022';

export function isGamePackageDuplicateNameError(res: {
  success: boolean;
  code?: string;
  status?: number;
}): boolean {
  return (
    res.success === false &&
    (res.code === ERR_GAME_PACKAGE_DUPLICATE_NAME || res.status === 409)
  );
}

export const createGamePackage = async (
  data: CreateGamePackageRequest
): Promise<CreateGamePackageResponse> => {
  try {
    const formData = new FormData();
    formData.append('game_id', data.game_id);
    formData.append('name', data.name);
    if (data.element_name !== undefined)
      formData.append('element_name', data.element_name);
    formData.append('description', data.description);
    formData.append('is_active', data.is_active.toString());

    if (data.shell_cost != null) formData.append('shell_cost', data.shell_cost.toString());
    if (data.image_file) {
      formData.append('image_file', data.image_file);
    }

    if (data.supplier_prices && data.supplier_prices.length > 0) {
      formData.append('supplier_prices', JSON.stringify(data.supplier_prices));
    }

    const response = await gamePackageAPI.post(
      '/admin/game-packages',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game package created successfully',
    };
  } catch (error: any) {
    const resData = error.response?.data;
    return {
      success: false,
      message:
        resData?.message || error.message || 'Failed to create game package',
      code: typeof resData?.code === 'string' ? resData.code : undefined,
      status:
        error.response?.status ??
        (typeof resData?.status === 'number' ? resData.status : undefined),
    };
  }
};

export interface UpdateGamePackageRequest {
  id: string;
  game_id?: string;
  name?: string;
  element_name?: string;
  description?: string;
  is_active?: boolean;
  shell_cost?: number | null;
  image_file?: File;
  remove_image?: boolean;
  supplier_prices?: SupplierPriceItem[];
}

export interface UpdateGamePackageResponse {
  success: boolean;
  data?: GamePackageData;
  message?: string;
  code?: string;
  status?: number;
}

export const updateGamePackage = async (
  data: UpdateGamePackageRequest
): Promise<UpdateGamePackageResponse> => {
  try {
    const formData = new FormData();

    if (data.game_id) formData.append('game_id', data.game_id);
    if (data.name) formData.append('name', data.name);
    if (data.element_name !== undefined)
      formData.append('element_name', data.element_name);
    if (data.description !== undefined)
      formData.append('description', data.description);
    if (data.is_active !== undefined)
      formData.append('is_active', data.is_active.toString());
    if (data.shell_cost != null) formData.append('shell_cost', data.shell_cost.toString());
    else if (data.shell_cost === null) formData.append('shell_cost', '');
    if (data.image_file) formData.append('image_file', data.image_file);
    if (data.remove_image) formData.append('remove_image', 'true');
    if (data.supplier_prices && data.supplier_prices.length > 0) {
      formData.append('supplier_prices', JSON.stringify(data.supplier_prices));
    }

    const response = await gamePackageAPI.put(
      `/admin/game-packages/${data.id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game package updated successfully',
    };
  } catch (error: any) {
    const resData = error.response?.data;
    return {
      success: false,
      message:
        resData?.message || error.message || 'Failed to update game package',
      code: typeof resData?.code === 'string' ? resData.code : undefined,
      status:
        error.response?.status ??
        (typeof resData?.status === 'number' ? resData.status : undefined),
    };
  }
};

export interface GetGamePackageByIdResponse {
  success: boolean;
  data?: GamePackageData;
  message?: string;
  /** Set when the request failed at the HTTP layer (axios error). */
  httpStatus?: number;
}

const isAbortLike = (error: unknown) => {
  const e = error as { code?: string; name?: string };
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError'
  );
};

export const getGamePackageById = async (
  id: string,
  opts?: { signal?: AbortSignal }
): Promise<GetGamePackageByIdResponse> => {
  try {
    const response = await gamePackageAPI.get(`/game-packages/${id}`, {
      skipGlobal500ErrorToast: true,
      signal: opts?.signal,
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game package fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    return {
      success: false,
      httpStatus: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game package',
    };
  }
};

export interface DeleteGamePackageResponse {
  success: boolean;
  message?: string;
}

export const deleteGamePackage = async (
  id: string
): Promise<DeleteGamePackageResponse> => {
  try {
    const response = await gamePackageAPI.delete(
      `/admin/game-packages/${id}/soft`
    );

    return {
      success: true,
      message: response.data.message || 'Package soft deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete game package',
    };
  }
};

export interface GamePackageStatsData {
  total: number;
  active: number;
  inactive: number;
}

export interface GetGamePackageStatsResponse {
  success: boolean;
  data?: GamePackageStatsData;
  message?: string;
}

export const getGamePackageStats =
  async (): Promise<GetGamePackageStatsResponse> => {
    try {
      const response = await gamePackageAPI.get('/admin/game-packages/stats');

      return {
        success: true,
        data: response.data,
        message: 'Package stats fetched successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch package stats',
      };
    }
  };

export interface PackageValues {
  id: string;
  name: string;
  price: number;
  source: string;
  game_id: string;
  supplier_prices: SupplierPriceItem[];
  is_active: boolean;
  created_at: string;
  is_deleted: boolean;
  updated_at: string;
  description: string;
  created_by_id: string;
  created_by_username: string;
}

export interface AuditLogItem {
  id: string;
  game_package_id: string;
  action: 'create' | 'update' | 'delete';
  old_values: PackageValues | null;
  new_values: PackageValues;
  changed_fields: string[] | null;
  performed_by_id: string;
  performed_by_username: string;
  created_at: string;
}

export interface GetAuditLogsRequest {
  package_id: string;
  action?: string;
  performed_by_id?: string;
  performed_by_username?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  page?: number;
}

export interface GetAuditLogsResponse {
  success: boolean;
  data?: {
    items: AuditLogItem[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
  };
  message?: string;
  httpStatus?: number;
}

export const getAuditLogsByPackageID = async (
  params: GetAuditLogsRequest,
  opts?: { signal?: AbortSignal }
): Promise<GetAuditLogsResponse> => {
  try {
    const {
      package_id,
      action,
      performed_by_id,
      performed_by_username,
      search,
      start_date,
      end_date,
      limit = 10,
      page = 1,
    } = params;

    const queryParams = new URLSearchParams();

    if (action) queryParams.append('action', action);
    if (performed_by_id) queryParams.append('performed_by_id', performed_by_id);
    if (performed_by_username)
      queryParams.append('performed_by_username', performed_by_username);
    if (search) queryParams.append('search', search);
    if (start_date) queryParams.append('start_date', start_date);
    if (end_date) queryParams.append('end_date', end_date);
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());

    const response = await gamePackageAPI.get(
      `/admin/game-packages/${package_id}/audit-logs?${queryParams.toString()}`,
      {
        skipGlobal500ErrorToast: true,
        signal: opts?.signal,
      }
    );

    return {
      success: true,
      data: {
        items: response.data.items,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
      },
      message: 'Audit logs fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    return {
      success: false,
      httpStatus: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch audit logs',
    };
  }
};

export interface UploadPackageCSVRow {
  row: number;
  game_name: string;
  game_id: string;
  name: string;
  description: string;
  supplier_name: string;
  topup_type_name: string;
  price: number;
  is_active: boolean;
  is_valid: boolean;
  error?: string;
}

export interface UploadPackageCSVResponse {
  success: boolean;
  data?: {
    success_count: number;
    failed_count: number;
    total_count: number;
    rows: UploadPackageCSVRow[];
  };
  message?: string;
}

export const uploadPackageCSV = async (
  file: File
): Promise<UploadPackageCSVResponse> => {
  try {
    const formData = new FormData();
    formData.append('csv_file', file);

    const response = await gamePackageAPI.post(
      '/admin/game-packages/upload-csv',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'CSV uploaded successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to upload CSV file',
    };
  }
};

export const getGamePackageOptions = async (
  params: GetGamePackageOptionsRequest
): Promise<GetGamePackageOptionsResponse> => {
  try {
    const {
      game_id,
      supplier_ids,
      game_topup_type_ids,
      include_deleted,
      include_inactive,
      for_topup,
    } = params;
    const body: Record<string, any> = {};

    if (game_id) {
      body.game_ids = Array.isArray(game_id) ? game_id : [game_id];
    }
    if (supplier_ids && supplier_ids.length > 0) {
      body.supplier_ids = supplier_ids;
    }
    if (game_topup_type_ids && game_topup_type_ids.length > 0) {
      body.game_topup_type_ids = game_topup_type_ids;
    }
    if (include_deleted) {
      body.include_deleted = true;
    }
    if (include_inactive) {
      body.include_inactive = true;
    }
    const queryParams = new URLSearchParams();
    if (for_topup) {
      queryParams.set('for_topup', 'true');
    }
    const query = queryParams.toString();
    const url = `/game-packages/options/search${query ? `?${query}` : ''}`;

    const response = await gamePackageAPI.post(url, body);

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game packages fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game packages',
    };
  }
};

// Sort order types
export interface PackageSortItem {
  id: string;
  name: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
}

export interface PackageSortOrderResponse {
  success: boolean;
  data?: {
    game_id: string;
    packages: PackageSortItem[];
  };
  message?: string;
}

export interface PackageOrderItem {
  package_id: string;
  sort_order: number;
}

export interface BatchUpdatePackageSortOrderRequest {
  game_id: string;
  PackageOrder: PackageOrderItem[];
}

export interface BatchUpdatePackageSortOrderResponse {
  success: boolean;
  message?: string;
}

export const getPackagesForSort = async (
  gameId: string
): Promise<PackageSortOrderResponse> => {
  try {
    const response = await gamePackageAPI.get(
      `/admin/game-packages/sort/${gameId}`
    );
    return {
      success: true,
      data: response.data,
      message: 'Packages fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch packages for sort',
    };
  }
};

export const updatePackageSortOrder = async (
  request: BatchUpdatePackageSortOrderRequest
): Promise<BatchUpdatePackageSortOrderResponse> => {
  try {
    const response = await gamePackageAPI.post(
      '/admin/game-packages/_order',
      request
    );
    return {
      success: true,
      message: response.data?.message || 'Sort order updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update sort order',
    };
  }
};

export default gamePackageAPI;
